import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  Modifier,
  ModifierData,
  ModifierType,
} from '@prlw/core/monitor/types/common';
import { ProfileController } from '@prlw/core/profile/profile.controller';
import { isEqual } from 'lodash';

type Sort = 'ASC' | 'DES' | null;
type LocalModifierType = 'basic' | 'prepaid' | 'defer' | 'simple';

@Component({
  selector: 'prlw-table-modifiers-overlay-ui',
  templateUrl: './modifiers-overlay.component.html',
  styleUrls: ['./modifiers-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModifiersOverlayComponent implements OnInit {
  @Input() public modifier!: Modifier;
  @Input() public sortDirection: Sort = null;
  @Output() public readonly closeOverlay = new EventEmitter<void>();
  @Output() public readonly setModifier = new EventEmitter<Modifier>();
  @Output() public readonly setSort = new EventEmitter<'ASC' | 'DES'>();
  public sort: Sort = null;
  public currentModifierType!: ModifierType;
  public currentModifierData: ModifierData | null = null;
  public prepaidMargin: string = '';
  public deferPercent: string = '';
  public deferPeriod: string = '';
  public userMode = '';

  constructor(
    @Inject(ProfileController)
    private readonly profileController: ProfileController,
  ) {
    this.profileController.profile$.subscribe((data) => {
      this.userMode = data.mode;
    });
  }

  public ngOnInit(): void {
    this.currentModifierType = this.modifier.type;
    this.sort = this.sortDirection;
    if (this.modifier.type === ModifierType.PREPAID) {
      this.prepaidMargin = String(this.modifier.data.prepaidMargin);
    }
    if (this.modifier.type === ModifierType.DEFER) {
      this.deferPeriod = String(this.modifier.data.deferPeriod);
      this.deferPercent = String(this.modifier.data.deferPercent);
    }
  }

  public get isBasicModifier(): boolean {
    return (
      isEqual(this.currentModifierType, ModifierType.BASIC) ||
      isEqual(this.currentModifierType, ModifierType.SIMPLE)
    );
  }

  public get isPrepaidModifier(): boolean {
    return this.currentModifierType === ModifierType.PREPAID;
  }

  public get isDeferModifier(): boolean {
    return this.currentModifierType === ModifierType.DEFER;
  }

  public get isApplyDisabled(): boolean {
    return (
      (this.isPrepaidModifier && !this.prepaidMargin) ||
      (this.isDeferModifier && (!this.deferPercent || !this.deferPeriod))
    );
  }

  public processKeyUp(event: KeyboardEvent): void {
    if (event.key && !/\d|\./.test(event.key)) {
      event.preventDefault();
    }
  }

  public processPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text');
    if (text && !/\d+\.?\d*/.test(text)) {
      event.preventDefault();
    }
  }

  public close(): void {
    this.closeOverlay.emit();
  }

  public closeModifierOverlay(event: boolean): void {
    if (event) {
      this.close();
    }
  }

  public apply(): void {
    this._setCurrentData();
    if (
      this.currentModifierType === ModifierType.BASIC ||
      this.currentModifierType === ModifierType.SIMPLE
    ) {
      this.setModifier.emit({
        type: this.currentModifierType,
      });
    }
    if (this.currentModifierType === ModifierType.PREPAID) {
      this.setModifier.emit({
        type: this.currentModifierType,
        data: {
          prepaidMargin: this.currentModifierData?.prepaidMargin ?? 0,
        },
      });
    }
    if (this.currentModifierType === ModifierType.DEFER) {
      this.setModifier.emit({
        type: this.currentModifierType,
        data: {
          deferPercent: this.currentModifierData?.deferPercent ?? 0,
          deferPeriod: this.currentModifierData?.deferPeriod ?? 0,
        },
      });
    }
    if (this.sort) {
      this.setSort.emit(this.sort);
    }
    this.close();
  }

  public updateSort(sort: Sort): void {
    this.sort = this.sort === sort ? null : sort;
  }

  public setCurrentModifier(modifierType: LocalModifierType): void {
    switch (modifierType) {
      case 'prepaid':
        this.currentModifierType = ModifierType.PREPAID;
        break;
      case 'defer':
        this.currentModifierType = ModifierType.DEFER;
        break;
      case 'basic':
      case 'simple':
        this.currentModifierType = ModifierType.SIMPLE;
        break;
      default:
        this.currentModifierType = ModifierType.BASIC;
    }
  }

  private _setCurrentData(): void {
    switch (this.currentModifierType) {
      case 'prepaid':
        this.currentModifierData = {
          prepaidMargin: Number(this.prepaidMargin),
        };
        break;
      case 'defer':
        this.currentModifierData = {
          deferPercent: Number(this.deferPercent),
          deferPeriod: Number(this.deferPeriod),
        };
        break;
      case 'basic':
      case 'simple':
        this.currentModifierType = ModifierType.SIMPLE;
        break;
      default:
        this.currentModifierData = null;
    }
  }

  @HostListener('window:keyup', ['$event'])
  private _onEnter(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.isApplyDisabled) {
      this.apply();
    }
  }
}
